import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { Firm, FirmsService } from 'api';
import { useResponse } from 'services/hooks/useResponse';
import { GENERATION_EFFORT_OPTIONS_QUERY_KEY } from './useGenerationEffortOptions';

export const FIRM_SCALAR_AI_SETTINGS_QUERY_KEY = 'FIRM_SCALAR_AI_SETTINGS';

export interface ScalarAiSettingsUpdate {
  max_allowed_effort?: string;
  default_effort?: string;
  document_auto_classification_enabled?: boolean;
  is_mcp_enabled?: boolean;
}

export const useGetFirmScalarAiSettings = (firmId?: number | null) =>
  useQuery({
    enabled: !isNil(firmId),
    queryFn: () => FirmsService.apiFirmsRead(firmId as number),
    queryKey: [FIRM_SCALAR_AI_SETTINGS_QUERY_KEY, firmId],
  });

// SCAL-9352: dedicated PATCH so saving never touches the legacy full-object
// PUT path (useUpdateFirmById), which clobbers unrelated fields, re-uploads
// images, and hard-navigates to Firm Profile on success.
export const useUpdateFirmScalarAiSettings = () => {
  const queryClient = useQueryClient();
  const { processErrorResponse } = useResponse();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ firmId, data }: { firmId: number; data: ScalarAiSettingsUpdate }) =>
      FirmsService.apiFirmsPartialUpdate(firmId, data as unknown as Firm),
  });

  const updateFirmScalarAiSettings = useCallback(
    async (firmId: number, data: ScalarAiSettingsUpdate) => {
      try {
        const updatedFirm = await mutateAsync({ firmId, data });
        // The effort ceiling is server-derived, so a mounted job-create dialog
        // holds a stale options response until this second key is invalidated.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [FIRM_SCALAR_AI_SETTINGS_QUERY_KEY, firmId] }),
          queryClient.invalidateQueries({ queryKey: [GENERATION_EFFORT_OPTIONS_QUERY_KEY, firmId] }),
        ]);
        return updatedFirm;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: 'An error occurred while saving the Scalar AI settings',
          action: 'save the Scalar AI settings',
        });
        return null;
      }
    },
    [mutateAsync, queryClient, processErrorResponse]
  );

  return { updateFirmScalarAiSettings, isPending };
};
